import React, { useState, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Grid, CircularProgress, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SchoolListItem } from '@beable/roster-backend-client-v1';
import CoursesTable from './CoursesTable';
import ClassesTable from './ClassesTable';
import dataViewService from '../../services/DataViewService';
import { getStyles } from './styles';

interface SchoolAccordionProps {
    siteId: number;
    handleClassSelect: any;
}

const initialSchoolsValue: SchoolListItem[] = [];

export default function SchoolAccordion(props: SchoolAccordionProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [isLoading, setIsLoading] = useState(false);
    const [schools, setSchools] = useState(initialSchoolsValue);
    const [expandedId, setExpandedId] = useState<string | false>(false);
    const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchSchools = async () => {
            setIsLoading(true);
            const fetchedSchools: SchoolListItem[] | null = await dataViewService.schoolsWithAcademicYearsAndCourses(props.siteId);
            setIsLoading(false);
            if (fetchedSchools) {
                setSchools(fetchedSchools);
            } else {
                setSchools(initialSchoolsValue);
                alert("Could not get the schools for this site. Please contact support...");
            }
        };

        fetchSchools();
    }, [props.siteId]);

    const handleChange = (key: string) => async (event: React.SyntheticEvent<Element, Event>, isExpanded: boolean) => {
        setExpandedId(isExpanded ? key : false);
    };

    const handleCourseSelect = (courseId: string) => {
        setSelectedCourseId(courseId);
    }

    return (
        <Box sx={styles.accordion_root}>
            {isLoading ?
                <Box sx={styles.loaderContainer}>
                    <CircularProgress disableShrink />
                </Box> :
                null}
            {schools && schools.length && !isLoading ? schools.map((school) => {
                const key = `${school.id}-${school.currentAcademicYear}-${props.siteId}`;
                return (
                    <Accordion
                        key={key}
                        expanded={expandedId === key}
                        onChange={handleChange(key)}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="course-accordion-content">
                            <Typography sx={styles.schoolName}>{school.name}</Typography>
                            <Typography sx={styles.schoolAcademicYear}>Academic year: {school.currentAcademicYear}</Typography>
                            <Typography sx={styles.schoolStatus}>Status: {school.internalStatus}</Typography>
                            <Typography sx={styles.schoolCourses}>Courses: {school.numberOfCourses}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid
                                container
                                size={{
                                    xs: 12,
                                    md: 5,
                                    lg: 5,
                                    xl: 5
                                }}>
                                {expandedId && expandedId === key ?
                                    <CoursesTable schoolId={school.id} siteId={props.siteId} onCourseSelect={handleCourseSelect}/> :
                                    null}
                            </Grid>
                            <Grid
                                container
                                size={{
                                    xs: 12,
                                    md: 2,
                                    lg: 2,
                                    xl: 2
                                }}>
                            </Grid>
                            <Grid
                                container
                                size={{
                                    xs: 12,
                                    md: 5,
                                    lg: 5,
                                    xl: 5
                                }}>
                                {expandedId && expandedId === key && selectedCourseId ?
                                    <ClassesTable
                                        courseId={selectedCourseId}
                                        schoolId={school.id}
                                        siteId={props.siteId}
                                        onClassSelect={props.handleClassSelect}/> :
                                    null}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                );
            }) :
                null}
        </Box>
    );
}
